import { BrowserModule } from '@angular/platform-browser';
import { NgModule } from '@angular/core';
import { AppRoutingModule } from './app-routing.module';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgxPaginationModule } from 'ngx-pagination';
import { ToastrModule } from 'ngx-toastr';
import { LazyLoadImageModule } from 'ng-lazyload-image';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { OVERLAY_DEFAULT_CONFIG } from '@angular/cdk/overlay';

import { AuthService } from './auth/auth.service';
import { AuthGuard } from './auth/auth.guard';

import { AppComponent } from './app.component';
import { LoginComponent } from './login/login.component';
import { PasswordChangeComponent } from './password-change/password-change.component';
import { HeaderComponent } from './header/header.component';
import { PageNotFoundComponent } from './page-not-found/page-not-found.component';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { ReloginDialogComponent } from './dialog/relogin-dialog/relogin-dialog.component';
import { PassCompDialogComponent } from './dialog/pass-comp-dialog/pass-comp-dialog.component';
import { GeneralYesNoDialogComponent } from './dialog/general-yes-no-dialog/general-yes-no-dialog.component';
import { GeneralMessageDialogComponent } from './dialog/general-message-dialog/general-message-dialog.component';
import { AccountEditDialogComponent } from './dialog/account-edit-dialog/account-edit-dialog.component';
import { AccountPasswordResetDialogComponent } from './dialog/account-password-reset-dialog/account-password-reset-dialog.component';
import { AccountDeleteDialogComponent } from './dialog/account-delete-dialog/account-delete-dialog.component';
import { PageAccessDeniedComponent } from './page-access-denied/page-access-denied.component';
import { ManagerEditDialogComponent } from './dialog/manager-edit-dialog/manager-edit-dialog.component';
import { ConcurrentEditDialogComponent } from './dialog/concurrent-edit-dialog/concurrent-edit-dialog.component';
import { TagSettingDialogComponent } from './dialog/tag-setting-dialog/tag-setting-dialog.component';
import { ChatWidgetComponent } from './chat-widget/chat-widget.component';
import { LinkyModule } from 'ngx-linky';
import { SignUpComponent } from './sign-up/sign-up.component';
import { OrgAboutComponent } from './organization-about/organization-about.component';

import { ToastUtil } from './util/toast-util';
import { CommonUtil } from './util/common-util';
import { ImageUtil } from './util/image-util';
import { MomentUtil } from './util/moment-util';

import { LpComponent } from './lp/lp.component';

import { MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS, MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { QuillModule } from 'ngx-quill';
import { MatTreeModule } from '@angular/material/tree';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { DragDropModule } from '@angular/cdk/drag-drop';
import 'moment/locale/ja';

// カレンダーフォーマット
export const MY_FORMATS = {
  parse: {
    dateInput: 'YYYY/MM/DD'
  },
  display: {
    dateInput: 'YYYY/MM/DD',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'YYYY/MM/DD',
    monthYearA11yLabel: 'MMMM YYYY'
  }
};

// quill 改行コード
import Quill from 'quill';
const Delta = Quill.import('delta') as any;
function lineBreakMatcher(node, delta) {
  var newDelta = new Delta();
  if (node.className !== "Apple-interchange-newline") {
    newDelta.insert({'sbreak': true});
  }
  return newDelta;
}

@NgModule({ declarations: [
        AppComponent,
        LoginComponent,
        PasswordChangeComponent,
        HeaderComponent,
        PageNotFoundComponent,
        ReloginDialogComponent,
        LpComponent,
        PassCompDialogComponent,
        MaintenanceComponent,
        GeneralYesNoDialogComponent,
        GeneralMessageDialogComponent,
        AccountEditDialogComponent,
        AccountPasswordResetDialogComponent,
        AccountDeleteDialogComponent,
        SignUpComponent,
        PageAccessDeniedComponent,
        ManagerEditDialogComponent,
        ConcurrentEditDialogComponent,
        TagSettingDialogComponent,
    ],
    bootstrap: [AppComponent], imports: [BrowserModule,
        AppRoutingModule,
        ChatWidgetComponent,
        BrowserAnimationsModule,
        FormsModule,
        ReactiveFormsModule,
        MatToolbarModule,
        MatSidenavModule,
        MatDividerModule,
        MatTableModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        MatCheckboxModule,
        MatSelectModule,
        MatButtonModule,
        MatIconModule,
        MatListModule,
        MatDialogModule,
        MatSliderModule,
        MatGridListModule,
        MatExpansionModule,
        MatProgressSpinnerModule,
        MatMenuModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatRadioModule,
        NgxPaginationModule,
        ToastrModule.forRoot(),
        LinkyModule,
        LazyLoadImageModule,
        PdfViewerModule,
        MatTreeModule,
        DragDropModule,
        QuillModule.forRoot({
            formats: ['bold', 'strike', 'blockquote', 'list', 'align', 'header', 'link', 'image', 'video', 'sbreak', 'toc-div', 'ogp-div', 'ogp-loading'],
            modules: {
                clipboard: {
                    matchVisual: false,
                    matchers: [
                        ['br', lineBreakMatcher]
                    ]
                },
                history: {
                    delay: 500,
                },
                keyboard: {
                    bindings: {
                        linebreak: {
                            key: 13,
                            handler: function (range) {
                                const prevLeaf = this.quill.getLeaf(range.index - 1)[0];
                                const currentLeaf = this.quill.getLeaf(range.index)[0];
                                const nextLeaf = this.quill.getLeaf(range.index + 1)[0];
                                if (currentLeaf.parent.domNode.nodeName === 'BLOCKQUOTE'
                                    || currentLeaf.parent.parent?.domNode.nodeName === 'BLOCKQUOTE'
                                    || currentLeaf.parent.parent?.parent?.domNode.nodeName === 'BLOCKQUOTE'
                                    || currentLeaf.parent.parent?.parent?.parent?.domNode.nodeName === 'BLOCKQUOTE') {
                                    if (currentLeaf.domNode.nodeName === 'BR' && !currentLeaf.prev && !currentLeaf.next) {
                                        // 空行のみ
                                        this.quill.removeFormat(range.index, 0, 'user');
                                        this.quill.insertText(range.index, '\n', 'user');
                                        this.quill.setSelection(range.index + 1, 0, 'user');
                                    }
                                    else if (currentLeaf.domNode.nodeName === 'BR' && (prevLeaf.domNode.nodeName === 'BR' || currentLeaf.next?.domNode.nodeName === 'BR' || nextLeaf?.domNode.nodeName === 'BR')) {
                                        // 引用内、改行が2回続いたら引用を終了する
                                        let cursor = range.index;
                                        if (nextLeaf.domNode.nodeName === 'BR') {
                                            this.quill.deleteText(cursor, 1, 'user');
                                        }
                                        this.quill.insertText(cursor, '\n', 'user');
                                        this.quill.removeFormat(cursor + 1, 0, 'user');
                                        // 後ろにテキストがある場合、2文字目にカーソルが当たってしまうことを防ぐ
                                        if (currentLeaf.next?.domNode.nodeName === '#text') {
                                            cursor--;
                                        }
                                        this.quill.setSelection(cursor + 1, 0, 'user');
                                    }
                                    else {
                                        let cursor = range.index;
                                        // 引用内の改行挿入
                                        this.quill.insertEmbed(cursor, 'sbreak', true, 'user');
                                        // Insert a second break if:
                                        if (nextLeaf === null || (currentLeaf.parent !== nextLeaf.parent &&
                                            nextLeaf.parent.constructor.name !== 'Blockquote' && nextLeaf.parent.parent?.constructor.name !== 'Blockquote' &&
                                            nextLeaf.parent.parent?.parent?.constructor.name !== 'Blockquote' && nextLeaf.parent.parent?.parent?.parent?.constructor.name !== 'Blockquote')) {
                                            this.quill.insertEmbed(cursor, 'sbreak', true, 'user');
                                        }
                                        this.quill.setSelection(cursor + 1, 0, 'user');
                                    }
                                }
                                else {
                                    this.quill.insertText(range.index, '\n', 'user');
                                    this.quill.setSelection(range.index + 1, 0, 'user');
                                }
                            }
                        },
                    }
                }
            }
        })], providers: [
        AuthService,
        AuthGuard,
        ToastUtil,
        CommonUtil,
        ImageUtil,
        MomentUtil,
        { provide: MAT_DATE_LOCALE, useValue: 'ja-JP' },
        {
            provide: OVERLAY_DEFAULT_CONFIG,
            useValue: {
              usePopover: false
            }
          },
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS]
        },
        { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
        {
            provide: MAT_PROGRESS_SPINNER_DEFAULT_OPTIONS,
            useValue: {
                diameter: 60,
            }
        },
        provideHttpClient(withInterceptorsFromDi())
    ] })
export class AppModule { }
